// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { LocationModel } from "@insite/client-framework/Common/Hooks/useLocationFilterSearch";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import AddressInfoCondensedDisplay, {
    AddressInfoCondensedDisplayStyles,
} from "@stoneage/Overrides/Components/AddressInfoCondensedDisplay";
import DistanceDisplay, { DistanceUnitOfMeasure } from "@stoneage/Overrides/Components/DistanceDisplay";
import { XLocationModel } from "@stoneage/Overrides/Components/LocationSearchForm";
import { fontConfig } from "@stoneage/XDL/constants";
import { ellipsis } from "@stoneage/XDL/Utilities";
import color from "@stoneage/XDL/Utilities/color";
import * as React from "react";
import { css } from "styled-components";

interface Props {
    isCurrentLocation?: boolean;
    location?: LocationModel & { properties?: { [key: string]: string } };
    distanceUnitOfMeasure?: DistanceUnitOfMeasure;
    distanceTitleOverride?: string;
    dealerDetailsUrl?: string;
    searchFilter?: string;
    extendedStyles?: LocationInfoWindowCardStyles;
    // XNG-Changes: navigates via the app's history (same as the results list) instead of a
    // plain href, so the last-search URL is preserved when the user later goes back
    onLocationSelected?: (event: React.MouseEvent<Element, MouseEvent>, location: XLocationModel) => void;
}

export interface LocationInfoWindowCardStyles {
    wrapper?: InjectableCss;
    distanceDisplayText?: TypographyPresentationProps;
    locationNameLink?: LinkPresentationProps;
    addressDisplay?: AddressInfoCondensedDisplayStyles;
    contactInfoWrapper?: InjectableCss;
    contactInfoLabel?: TypographyPresentationProps;
    contactInfoValue?: LinkPresentationProps;
    locationTypeWrapper?: InjectableCss;
    locationTypeText?: TypographyPresentationProps;
    locationTypeSlash?: TypographyPresentationProps;
}

const fontSizeStyle = fontConfig.xSmallSingleLine;

const addressDisplayValue: TypographyPresentationProps = {
    ...fontSizeStyle,
    css: css`
        margin-bottom: 0;
        line-height: ${fontConfig.xSmallSingleLine.lineHeight};
    `,
};

export const locationInfoWindowCardStyles: LocationInfoWindowCardStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: 8px;
            min-width: 200px;
            max-width: 250px;
            padding-bottom: 1px; // To prevent show scrollbar
            padding-top: 12px;
        `,
    },
    distanceDisplayText: {
        ...fontSizeStyle,
        weight: fontConfig.weight.medium,
        css: css`
            margin-bottom: 0;
        `,
    },
    locationNameLink: {
        typographyProps: {
            ...fontSizeStyle,
            weight: fontConfig.weight.medium,
        },
    },
    addressDisplay: {
        wrapper: {
            css: css`
                font-style: normal;
            `,
        },
        addressText: {
            ...addressDisplayValue,
        },
        countryText: {
            ...addressDisplayValue,
        },
        cityStatePostalCodeText: {
            ...addressDisplayValue,
        },
    },
    contactInfoWrapper: {
        css: css`
            display: flex;
            gap: 4px;
            align-items: center;
        `,
    },
    contactInfoLabel: {
        ...fontSizeStyle,
        css: css`
            white-space: nowrap;
        `,
    },
    contactInfoValue: {
        typographyProps: {
            ...fontSizeStyle,
        },
        css: css`
            ${ellipsis(1)}
        `,
    },
    locationTypeWrapper: {
        css: css`
            display: flex;
            gap: 8px;
            flex-wrap: wrap;
            padding-top: 12px;
            margin-top: 4px;
            border-top: 1px solid ${color("common.border")};
            width: 100%;
        `,
    },
    locationTypeText: {
        color: "common.ui1",
        ...fontConfig.xxSmallSingleLine,
        weight: fontConfig.weight.medium,
    },
    locationTypeSlash: {
        color: "common.ui1",
        ...fontConfig.xxSmallSingleLine,
        weight: fontConfig.weight.regular,
    },
};

const LocationInfoWindowCard: React.FC<Props> = ({
    isCurrentLocation,
    location,
    distanceUnitOfMeasure,
    distanceTitleOverride,
    dealerDetailsUrl,
    extendedStyles,
    searchFilter,
    onLocationSelected,
}) => {
    const [styles] = React.useState(() => mergeToNew(locationInfoWindowCardStyles, extendedStyles));

    if (isCurrentLocation) {
        return (
            <StyledWrapper {...styles.wrapper}>
                <Typography {...styles.distanceDisplayText}>{translate("Current Location")}</Typography>
                {searchFilter && <Typography {...styles.locationTypeText}>{searchFilter}</Typography>}
            </StyledWrapper>
        );
    }

    if (!location || !distanceUnitOfMeasure) {
        return null;
    }

    return (
        <StyledWrapper {...styles.wrapper} data-test-selector="locationInfoWindow">
            {location.distance > 0.01 && (
                <DistanceDisplay
                    {...styles.distanceDisplayText}
                    distance={location.distance}
                    unitOfMeasure={distanceUnitOfMeasure}
                    overrideTitle={distanceTitleOverride}
                />
            )}
            <Link
                onClick={e => {
                    onLocationSelected?.(e, location);
                }}
                {...styles.locationNameLink}
            >
                {location.name}
            </Link>
            <AddressInfoCondensedDisplay {...location} extendedStyles={styles.addressDisplay} />
            {location.phone && (
                <StyledWrapper {...styles.contactInfoWrapper}>
                    <Typography {...styles.contactInfoLabel}>{translate("Phone")}:</Typography>
                    <Link {...styles.contactInfoValue} href={`tel:${location.phone}`} target="_blank">
                        {location.phone}
                    </Link>
                </StyledWrapper>
            )}
            {location.properties?.email && (
                <StyledWrapper {...styles.contactInfoWrapper}>
                    <Typography {...styles.contactInfoLabel}>{translate("Email")}:</Typography>
                    <Link {...styles.contactInfoValue} href={`mailto:${location.properties.email}`} target="_blank">
                        {location.properties.email}
                    </Link>
                </StyledWrapper>
            )}
            {location.properties?.locationType && (
                <StyledWrapper {...styles.locationTypeWrapper}>
                    {location.properties.locationType.split("|").map((type, index, array) => (
                        <React.Fragment key={type}>
                            <Typography {...styles.locationTypeText}>{type}</Typography>
                            {index !== array.length - 1 && <Typography {...styles.locationTypeSlash}>|</Typography>}
                        </React.Fragment>
                    ))}
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

export default LocationInfoWindowCard;
